import '@material/web/icon/icon.js'
import '@operato/data-grist'
import '@operato/context/ox-context-page-toolbar.js'
import './data-summary-view.js'
import moment from '@operato/moment-timezone-es'

import gql from 'graphql-tag'
import { css, html } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { connect } from 'pwa-helpers/connect-mixin'

import {
  getEditor,
  getRenderer,
  ColumnConfig,
  DataGrist,
  FetchOption,
  SortersControl,
  GristRecord
} from '@operato/data-grist'
import { client } from '@operato/graphql'
import { i18next, localize } from '@operato/i18n'
import { openPopup } from '@operato/layout'
import { PageView, store } from '@operato/shell'
import { CommonHeaderStyles, ScrollbarStyles } from '@operato/styles'
import { isMobileDevice } from '@operato/utils'
import { OxPopup } from '@operato/popup'
import { DataItem } from '@operato/dataset'

enum DataSetSummaryPeriodType {
  Hour = 'hour',
  WorkShift = 'work-shift',
  WorkDate = 'work-date',
  Day = 'day'
}

@customElement('data-summary-search-page')
export class DataSummarySearchPage extends connect(store)(localize(i18next)(PageView)) {
  static styles = [
    ScrollbarStyles,
    CommonHeaderStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;

        overflow: hidden;
      }

      ox-grist {
        overflow-y: auto;
        flex: 1;
      }

      .header {
        grid-template-areas: 'filters actions';
      }
    `
  ]

  @state() dataSetId?: string

  @state() dataSet: any
  @state() dataKeySet: any
  @state() dataItems?: DataItem[]

  @state() gristConfig: any
  @state() mode: 'CARD' | 'GRID' | 'LIST' = isMobileDevice() ? 'CARD' : 'GRID'

  @query('ox-grist') private grist!: DataGrist

  get context() {
    return {
      title: i18next.t('title.data-summary search'),
      search: {
        handler: (search: string) => {
          this.grist.searchText = search
        },
        value: this.grist?.searchText || ''
      },
      filter: {
        handler: () => {
          this.grist.toggleHeadroom()
        }
      },
      help: 'dataset/data-summary-search',
      actions: [],
      exportable: {
        name: i18next.t('title.data-summary search'),
        data: this._exportableData.bind(this)
      },
      toolbar: false
    }
  }

  render() {
    const mode = this.mode || (isMobileDevice() ? 'LIST' : 'GRID')

    return html`
      <ox-grist .mode=${mode} .config=${this.gristConfig} .fetchHandler=${this.fetchHandler.bind(this)}>
        <div slot="headroom" class="header">
          <div class="filters">
            <ox-filters-form autofocus without-search></ox-filters-form>
          </div>

          <ox-context-page-toolbar class="actions" .context=${this.context}></ox-context-page-toolbar>
        </div>
      </ox-grist>
    `
  }

  pageUpdated(changes, lifecycle) {
    if (this.active) {
      this.dataSetId = lifecycle.resourceId
      return
    }
  }

  async updated(changes) {
    if (changes.has('dataSetId')) {
      const response = await client.query({
        query: gql`
          query ($id: String!) {
            dataSet(id: $id) {
              id
              summaryPeriod
              dataItems {
                name
                description
                active
                hidden
                tag
                group
                type
                unit
                options
                quota
                spec
                stat
              }
              dataKeySet {
                id
                name
                description
                dataKeyItems {
                  name
                  description
                  dataKey
                  tKey
                }
              }
            }
          }
        `,
        variables: {
          id: this.dataSetId
        }
      })

      this.dataSet = response.data.dataSet
      this.dataKeySet = this.dataSet?.dataKeySet || {}
      this.dataItems = this.dataSet?.dataItems || []

      this.refreshGristConfig()

      this.grist.fetch()
    }
  }

  getKeyColumns() {
    return (
      this.dataKeySet?.dataKeyItems.map((item, index) => {
        return {
          type: 'string',
          name: `key0${index + 1}`,
          header: i18next.t(item.tKey),
          record: {
            editable: false
          },
          sortable: true,
          filter: 'i_like',
          width: 120,
          imex: true
        }
      }) || []
    )
  }

  getDataColumns() {
    return (
      this.dataItems
        ?.filter(dataItem => dataItem.active && dataItem.stat)
        .map((item, index) => {
          return {
            type: 'number',
            name: `data0${index + 1}`,
            header: i18next.t(item.name),
            record: {
              editable: false
            },
            width: 120,
            imex: true
          }
        }) || []
    )
  }

  refreshGristConfig() {
    const yesterday = moment().subtract(1, 'day').format('YYYY-MM-DD')
    const today = moment().format('YYYY-MM-DD')

    this.gristConfig = {
      list: { fields: ['dataSet', 'data', 'updater', 'updatedAt'] },
      columns: [
        { type: 'gutter', gutterName: 'sequence' },
        { type: 'gutter', gutterName: 'row-selector', multiple: true },
        {
          type: 'gutter',
          gutterName: 'button',
          icon: 'assignment',
          title: i18next.t('title.open data summary view'),
          handlers: {
            click: (columns, data, column, record, rowIndex) => {
              openPopup(
                html`
                  <data-summary-view data-summary-id=${record.id} style="background-color: white;"></data-summary-view>
                `,
                {
                  backdrop: true,
                  size: 'large',
                  title: i18next.t('title.data-summary view')
                }
              )
            }
          }
        },
        {
          type: 'string',
          name: 'date',
          header: i18next.t('field.date'),
          sortable: true,
          filter: {
            type: 'date',
            operator: 'between',
            value: [yesterday, today]
          },
          width: 80,
          imex: true
        },
        (this.dataSet.summaryPeriod == DataSetSummaryPeriodType.Hour ||
          this.dataSet.summaryPeriod == DataSetSummaryPeriodType.WorkShift) && {
          type: 'string',
          name: 'period',
          header: i18next.t('field.time-period'),
          sortable: true,
          width: 60,
          imex: true
        },
        ...this.getKeyColumns(),
        ...this.getDataColumns(),
        {
          type: 'json5',
          name: 'data',
          header: i18next.t('field.data'),
          record: {
            editable: false
          },
          hidden: true,
          imex: true
        },
        {
          type: 'number',
          name: 'count',
          header: i18next.t('field.count'),
          record: {
            editable: false
          },
          width: 30
        },
        {
          type: 'number',
          name: 'countOoc',
          header: i18next.t('field.count-ooc'),
          record: {
            editable: false
          },
          width: 30
        },
        {
          type: 'number',
          name: 'countOos',
          header: i18next.t('field.count-oos'),
          record: {
            editable: false
          },
          width: 30
        },
        {
          type: 'resource-object',
          name: 'updater',
          header: i18next.t('field.updater'),
          sortable: true,
          width: 120,
          imex: true
        },
        {
          type: 'datetime',
          name: 'updatedAt',
          header: i18next.t('field.updated_at'),
          sortable: true,
          width: 180,
          imex: true
        }
      ].filter(Boolean),
      rows: {
        appendable: false,
        selectable: {
          multiple: true
        }
      },
      sorters: [
        {
          name: 'date',
          desc: true
        }
      ]
    }
  }

  async fetchHandler({ page, limit, sortings = [], filters = [] }: FetchOption) {
    if (!this.dataSetId) {
      return { records: [], total: 0 }
    }

    const response = await client.query({
      query: gql`
        query ($dataSetId: String!, $filters: [Filter!], $pagination: Pagination, $sortings: [Sorting!]) {
          dataSummariesByDataSet(
            dataSetId: $dataSetId
            filters: $filters
            pagination: $pagination
            sortings: $sortings
          ) {
            items {
              id
              name
              description
              date
              period
              key01
              key02
              key03
              key04
              key05
              data01
              data02
              data03
              data04
              data05
              summary
              count
              countOoc
              countOos
              updater {
                id
                name
              }
              updatedAt
            }
            total
          }
        }
      `,
      variables: {
        dataSetId: this.dataSetId,
        filters,
        pagination: { page, limit },
        sortings
      }
    })

    return {
      total: response.data.dataSummariesByDataSet.total || 0,
      records: response.data.dataSummariesByDataSet.items || []
    }
  }

  _exportableData() {
    let records = [] as GristRecord[]
    if (this.grist.selected && this.grist.selected.length > 0) {
      records = this.grist.selected
    } else {
      records = this.grist.data.records
    }

    var headerSetting = this.grist.compiledConfig.columns
      .filter(column => column.type !== 'gutter' && column.record !== undefined && column.imex !== undefined)
      .map(column => {
        return column.imex === true
          ? {
              header: column.header.renderer(column),
              key: column.name,
              width: column.width,
              type: column.type
            }
          : column.imex
      })

    var data = records.map(item => {
      return {
        id: item.id,
        ...this.gristConfig.columns
          .filter(column => column.type !== 'gutter' && column.record !== undefined && column.imex !== undefined)
          .reduce((record, column) => {
            const key = column.imex === true ? column.name : column.imex.key
            record[key] = key
              .split('.')
              .reduce((obj, key) => (obj && obj[key] !== 'undefined' ? obj[key] : undefined), item)
            return record
          }, {})
      }
    })

    return { header: headerSetting, data: data }
  }
}
